import React, { useState, useRef, useEffect } from "react";
import {
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-material-ui";
import * as Yup from "yup";
import clsx from "clsx";
import MuiPhoneNumber from "material-ui-phone-number";
import { isEqual } from "lodash";
import apiClient from "../../../auth/apiClient";
import MobilePaymentService from "../../../services/MobilePaymentService";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import {
  isValidEmail,
  isValidMobile,
} from "../../../components/MobilePayment/Receipts/receiptValidation";
import { useSendReceiptPanelStyles } from "./SendReceiptPanel.styles";

// The panel instantiates the service once at module scope, matching the
// convention in SendReceiptForm.js / EmailRecieptForm.js. The apiClient carries
// the mobile-pay auth token acquired by the SendReceiptPage before this panel
// renders.
const mobilePayService = new MobilePaymentService(apiClient);

// After a fully successful send the SEND button is held disabled briefly to
// prevent accidental repeat sends, then automatically re-enabled once this
// cooldown elapses (even if the user has not changed anything).
const RESEND_COOLDOWN_MS = 3000;

/**
 * Validation schema for the Send Receipt form, following the app-standard
 * Formik + Yup pattern (see FacilityEditor.js). Validation is conditional on
 * the delivery-method checkboxes: the email field is required and format-checked
 * only when "Send via Email" is selected, and the phone field only when "Send
 * via Text" is selected. An unchecked channel is neither required nor validated.
 *
 * The format checks reuse the shared pure validators (`isValidEmail`,
 * `isValidMobile`) so the client rules stay identical to the rest of the
 * receipt-send feature; `isValidMobile` strips the MuiPhoneNumber mask
 * formatting, and the formatted value also satisfies the SMS endpoint regex.
 */
export const SendReceiptSchema = Yup.object().shape({
  sendEmail: Yup.boolean(),
  sendText: Yup.boolean(),
  email: Yup.string().when("sendEmail", {
    is: true,
    then: Yup.string()
      .required("Required")
      .test(
        "valid-receipt-email",
        "Enter a valid email address",
        (value) => isValidEmail(value)
      ),
    otherwise: Yup.string(),
  }),
  phone: Yup.string().when("sendText", {
    is: true,
    then: Yup.string()
      .required("Required")
      .test(
        "valid-receipt-mobile",
        "Enter a valid phone number",
        (value) => isValidMobile(value)
      ),
    otherwise: Yup.string(),
  }),
});

/**
 * SendReceiptPanel - the form body of the dedicated Send Receipt Page.
 *
 * Presents an email field, a phone field (the app-standard MuiPhoneNumber
 * masked widget), two independently-selectable delivery-method checkboxes
 * ("Send via Email" / "Send via Text"), and a single SEND button. Values are
 * held in Formik (`email`, `phone`, `sendEmail`, `sendText`) as a single source
 * of truth, validated with the Yup `SendReceiptSchema` above (the app-standard
 * Formik + Yup validation approach).
 *
 * The delivery methods are rendered as checkboxes (a `FormGroup` of
 * `Checkbox`es), never as toggle Switches. The email field is `variant="outlined"`
 * with a real label; the phone field is the masked MuiPhoneNumber widget.
 *
 * Behavior:
 * - Validation is conditional per the schema: a checked channel requires a valid
 *   value; an unchecked channel is not validated. Formik surfaces the field-level
 *   error messages ("Required" for an empty checked field; "Enter a valid email
 *   address" / "Enter a valid phone number" for an invalid value).
 * - SEND is disabled until at least one channel is checked and the form is valid
 *   (Formik `isValid` against the schema).
 * - Each selected + valid channel is sent independently (one failing does not
 *   abort the other); each attempt emits its own channel-specific toast.
 * - SEND is disabled while a send is in flight and stays disabled after a fully
 *   successful send (anti-spam). It re-enables if any attempted channel failed
 *   (retry) or once the user edits the form away from the last-sent values.
 * - Values + selection are retained on failure; no email/phone value (PII) is
 *   ever logged.
 *
 * @param {object} props Component props.
 * @param {string} props.ticketId The ticket identifier the receipt is for.
 * @returns {JSX.Element} The rendered Send Receipt form body.
 */
const SendReceiptPanel = ({ ticketId }) => {
  const classes = useSendReceiptPanelStyles();
  const enqueueSnackbar = useEnqueueSnackbar();

  // Anti-spam state. `isSending` blocks repeated presses while a send is in
  // flight. `lastSentValues` holds the form snapshot of the most recent fully
  // successful send; SEND stays disabled while the current values still equal
  // that snapshot (so the identical receipt is not sent again), and naturally
  // re-enables once the user edits any field or checkbox. A failed send does
  // not update the snapshot, so SEND remains available for retry.
  const [isSending, setIsSending] = useState(false);
  const [lastSentValues, setLastSentValues] = useState(null);
  // Holds the pending re-enable timer so it can be cleared (avoids stacking
  // timers across sends and cleans up on unmount).
  const resendTimerRef = useRef(null);

  // Clear any pending re-enable timer when the panel unmounts.
  useEffect(
    () => () => {
      if (resendTimerRef.current) {
        clearTimeout(resendTimerRef.current);
      }
    },
    []
  );

  /**
   * Attempts each selected + valid delivery channel independently and emits one
   * channel-specific toast per attempt. Tracks in-flight and last-sent state so
   * the SEND button can be restricted against repeated presses.
   *
   * @param {object} values The current Formik values.
   */
  const onSend = async (values) => {
    const emailTrim = (values.email || "").trim();
    const phoneTrim = (values.phone || "").trim();

    const attempts = [];
    if (values.sendEmail && isValidEmail(emailTrim)) {
      attempts.push({
        run: () => mobilePayService.emailReceipt(ticketId, emailTrim),
        successCopy: "Receipt Email Sent",
        failureCopy: "Failed to Send Email",
        successTag: "receiptEmailSent",
        failureTag: "receiptEmailFailed",
      });
    }
    if (values.sendText && isValidMobile(phoneTrim)) {
      attempts.push({
        run: () =>
          mobilePayService.sendSMSReceipt({ TicketID: ticketId, To: phoneTrim }),
        successCopy: "Receipt Text Sent",
        failureCopy: "Failed to Send Text",
        successTag: "receiptTextSent",
        failureTag: "receiptTextFailed",
      });
    }

    if (attempts.length === 0) {
      return;
    }

    // Mark in-flight so repeated clicks during the request are blocked.
    setIsSending(true);

    // Run channels independently; one channel failing must not abort the other.
    // The catch intentionally discards the error object - no email/phone value
    // (PII) is ever logged, and toasts carry only the fixed channel copy.
    const results = await Promise.all(
      attempts.map(async (attempt) => {
        try {
          await attempt.run();
          enqueueSnackbar(attempt.successCopy, {
            variant: "success",
            tag: attempt.successTag,
          });
          return true;
        } catch {
          enqueueSnackbar(attempt.failureCopy, {
            variant: "error",
            tag: attempt.failureTag,
          });
          return false;
        }
      })
    );

    const allSucceeded = results.every((ok) => ok);
    // On a fully successful send, snapshot the sent values so SEND stays
    // disabled until the user changes something. On any failure (including a
    // partial failure) leave the snapshot unset so SEND stays available for
    // retry; Formik values and the delivery selection are not reset here.
    if (allSucceeded) {
      setLastSentValues({ ...values });
      // Hold SEND disabled, then automatically re-enable it after the cooldown
      // (clearing the snapshot) so the user can resend without editing. Clear any
      // prior timer first so repeated sends do not stack timers.
      if (resendTimerRef.current) {
        clearTimeout(resendTimerRef.current);
      }
      resendTimerRef.current = setTimeout(() => {
        setLastSentValues(null);
        resendTimerRef.current = null;
      }, RESEND_COOLDOWN_MS);
    }
    setIsSending(false);
  };

  return (
    <Formik
      initialValues={{ email: "", phone: "", sendEmail: false, sendText: false }}
      validationSchema={SendReceiptSchema}
      validateOnMount={true}
      onSubmit={onSend}
    >
      {({ values, errors, touched, handleChange, setFieldValue, isValid }) => {
        const anySelected = values.sendEmail || values.sendText;

        // Anti-spam: once a send fully succeeds, keep SEND disabled while the
        // current values still equal the last-sent snapshot. Any edit changes
        // `values`, so the comparison fails and SEND re-enables.
        const alreadySent =
          lastSentValues !== null && isEqual(values, lastSentValues);

        // SEND is disabled unless a channel is selected and the form passes the
        // Yup schema (isValid), and is additionally blocked while in flight or
        // immediately after a fully successful send.
        const sendDisabled =
          !(anySelected && isValid) || isSending || alreadySent;

        return (
          <Form
            className={clsx(classes.form)}
            data-testid="send-receipt-panel"
            data-ticket-id={ticketId}
          >
            <div className={clsx(classes.fieldGroup)}>
              <Field
                component={TextField}
                className={clsx(classes.input)}
                variant="outlined"
                name="email"
                type="email"
                label="Email Address"
                fullWidth
                id="receipt-email"
                data-id="receipt-email"
                error={values.sendEmail && Boolean(errors.email)}
                helperText={values.sendEmail ? errors.email : ""}
                FormHelperTextProps={{ "data-testid": "email-error" }}
              />
              <FormGroup className={clsx(classes.checkboxGroup)}>
                <FormControlLabel
                  control={
                    <Checkbox
                      className="send-email-checkbox"
                      color="primary"
                      name="sendEmail"
                      checked={values.sendEmail}
                      onChange={handleChange}
                      data-id="send-via-email"
                    />
                  }
                  label="Send via Email"
                />
              </FormGroup>
            </div>

            <div className={clsx(classes.fieldGroup)}>
              <MuiPhoneNumber
                className={clsx(classes.input)}
                variant="outlined"
                label="Phone Number"
                name="phone"
                data-id="receipt-phone"
                defaultCountry={"us"}
                onlyCountries={["us"]}
                value={values.phone}
                onChange={(value) => setFieldValue("phone", value)}
                error={values.sendText && Boolean(errors.phone)}
                helperText={values.sendText ? errors.phone : ""}
                FormHelperTextProps={{ "data-testid": "phone-error" }}
                fullWidth
              />
              <FormGroup className={clsx(classes.checkboxGroup)}>
                <FormControlLabel
                  control={
                    <Checkbox
                      className="send-text-checkbox"
                      color="primary"
                      name="sendText"
                      checked={values.sendText}
                      onChange={handleChange}
                      data-id="send-via-text"
                    />
                  }
                  label="Send via Text"
                />
              </FormGroup>
            </div>

            <Button
              className={clsx(classes.sendButton)}
              type="submit"
              variant="contained"
              color="primary"
              data-testid="send-button"
              aria-label="send-receipt-button"
              disabled={sendDisabled}
            >
              SEND
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

export default SendReceiptPanel;